import {
  SocialWallBoard,
  SocialWallExternalResult,
  SocialWallProvider,
  SocialWallStream,
} from "../../../cores/socialWall/interfaces";

const STORAGE_KEY = "wembii.socialWall.boards.v1";

function uid() {
  return `sw_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function defaultStreamTitle(
  provider: SocialWallProvider | string,
  streamType: string = "my_posts"
): string {
  if (streamType === "external") {
    switch (provider) {
      case "youtube":
        return "Canal competencia";
      case "instagram":
        return "Perfil competencia";
      default:
        return "Página competencia";
    }
  }
  switch (provider) {
    case "youtube":
    case "tiktok":
      return "Mis videos";
    default:
      return "Mis posts";
  }
}

export function createStream(
  provider: SocialWallProvider | string,
  accountName?: string,
  accountAvatar?: string
): SocialWallStream {
  return {
    id: uid(),
    provider,
    streamType: "my_posts",
    title: defaultStreamTitle(provider),
    accountName,
    accountAvatar,
  };
}

export function createExternalStream(
  result: SocialWallExternalResult
): SocialWallStream {
  return {
    id: uid(),
    provider: result.provider,
    streamType: "external",
    title: defaultStreamTitle(result.provider, "external"),
    accountName: result.name,
    accountAvatar: result.avatarUrl,
    externalId: result.accountId,
    externalUsername: result.username || undefined,
  };
}

export function createBoard(
  name: string,
  streams: SocialWallStream[] = []
): SocialWallBoard {
  return {
    id: uid(),
    name,
    streams,
  };
}

const LEGACY_BOARD_NAMES: Record<string, string> = {
  "Mis muros": "Mis publicaciones",
  "Meus muros": "Minhas publicações",
  "My streams": "My posts",
};

const LEGACY_STREAM_TITLES: Record<string, string> = {
  "My Posts": "Mis posts",
  "My Videos": "Mis videos",
  "My posts": "Mis posts",
  "My videos": "Mis videos",
  "Mis posteos": "Mis posts",
};

function migrateBoardName(name: string): string {
  return LEGACY_BOARD_NAMES[name] || name;
}

function migrateStreamTitle(title: string): string {
  return LEGACY_STREAM_TITLES[title] || title;
}

export function loadBoardsFromStorage(
  companyId: string
): SocialWallBoard[] | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}.${companyId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((board: SocialWallBoard) => ({
      ...board,
      name: migrateBoardName(board.name),
      streams: (board.streams || []).map((stream) => ({
        ...stream,
        title: migrateStreamTitle(stream.title),
      })),
    }));
  } catch {
    return null;
  }
}

export function saveBoardsToStorage(
  companyId: string,
  boards: SocialWallBoard[]
) {
  try {
    localStorage.setItem(`${STORAGE_KEY}.${companyId}`, JSON.stringify(boards));
  } catch {
    /* ignore quota */
  }
}

export const PROVIDER_META: Record<
  string,
  { label: string; color: string; icon: string }
> = {
  facebook: { label: "Facebook", color: "#1877F2", icon: "facebook" },
  instagram: { label: "Instagram", color: "#E4405F", icon: "instagram" },
  tiktok: { label: "TikTok", color: "#111111", icon: "tiktok" },
  linkedin_business: {
    label: "LinkedIn",
    color: "#0A66C2",
    icon: "linkedin",
  },
  youtube: { label: "YouTube", color: "#FF0000", icon: "youtube" },
};

export const EXTERNAL_PROVIDERS: SocialWallProvider[] = [
  "facebook",
  "instagram",
  "youtube",
];
